import { IProfile } from "@/interfaces/Iprofile";
import { baseApi } from "./api";
import { useQuery, useMutation } from "@tanstack/react-query";
import { IBusiness } from "@/interfaces/Ibusiness";
import { validateZipcode } from "@/utils/validation";

const AUTH_ROUTES = {
  LOGIN: "/auth/login",
  REGISTER: "/auth/register",
  VERIFY_OTP: "/auth/verify-otp",
  VERIFY_EMAIL: "/auth/verify-email",
  COMPLETE_PERSONAL_DATA: "/auth/register/complete-personal-data",
  GOOGLE_SUCCESS: "/auth/social/get-tokens",
  GET_USER_STATE: "/auth/register/state",
  LOGOUT: "/auth/logout",
  GET_ME: "/auth/me",
  DELETE_ACCOUNT: "/auth/",
  GET_AVAILABLE_PHONE_NUMBERS: "/business-lines/available-phone-numbers",
  GET_AVAILABLE_PACKAGES: "/available-packages",
  PURCHASE_PACKAGE: "/packages/:id/purchase?phone_number=:phone_number",
  PURCHASE_PACKAGE_SUCCESS: "/packages/purchase/success?session_id=:session_id",
  PROFILE: "/auth/profile",
  BUSINESS: "/business/",
};

const authApi = {
  getMe: async () => {
    const response = await baseApi.get(AUTH_ROUTES.GET_ME);
    return response;
  },

  loginByEmailPassword: async (email: string, password: string) => {
    const response = await baseApi.post(AUTH_ROUTES.LOGIN, {
      email,
      password,
    });
    return response;
  },

  registerByEmailPassword: async (
    email: string,
    password: string,
    confirm_password: string
  ) => {
    const response = await baseApi.post(AUTH_ROUTES.REGISTER, {
      email,
      password,
      confirm_password,
    });
    return response;
  },

  verifyOTP: async (email: string, otp: string) => {
    const response = await baseApi.post(AUTH_ROUTES.VERIFY_OTP, {
      email,
      otp,
    });
    return response;
  },

  verifyEmail: async (token: string) => {
    const response = await baseApi.post(`${AUTH_ROUTES.VERIFY_EMAIL}/${token}`);
    return response;
  },

  completePersonalData: async (firstName: string, lastName: string) => {
    const data = {
      timezone: "UTC",
      first_name: firstName,
      last_name: lastName,
      working_hours: {
        mo: null,
        tu: null,
        we: null,
        th: null,
        fr: null,
        sa: null,
        su: null,
      },
      phones: [],
      other_emails: [],
      about: null,
      social_links: {
        website: null,
        facebook: null,
        twitter: null,
        linkedin: null,
        instagram: null,
        youtube: null,
      },
      pronoun: null,
      role: null,
    };

    const response = await baseApi.put(AUTH_ROUTES.PROFILE, data);
    return response;
  },

  completeBusinessData: async (
    Name: string,
    Emails: string[],
    Phones: string[],
    address: {
      country?: string;
      city: string;
      state: string;
      line_1: string;
      line_2?: string | null;
      zip_code?: string;
    }
  ) => {
    const data: IBusiness = {
      timezone: "UTC",
      business_name: Name,
      phones: Phones,
      emails: Emails,
      social_links: {
        website: null,
        facebook: null,
        twitter: null,
        linkedin: null,
        instagram: null,
        youtube: null,
      },
      about: null,
      working_hours: {
        mo: null,
        tu: null,
        we: null,
        th: null,
        fr: null,
        sa: null,
        su: null,
      },
    };

    // Ensure address has required fields
    if (address && address.line_1) {
      data.address = {
        country: address.country || null,
        city: address.city,
        state: address.state,
        line_1: address.line_1,
        line_2: address.line_2 || null,
        zip_code: address.zip_code || null,
      };
    }

    try {
      const response = await baseApi.put(AUTH_ROUTES.BUSINESS, data);
      return response;
    } catch (error: any) {
      // Handle 422 validation errors specifically
      if (error.response?.status === 422) {
        const errorMessage =
          error.response?.data?.detail?.[0]?.msg ||
          "Business data validation failed. Please check your input.";
        throw new Error(errorMessage);
      }
      throw error;
    }
  },

  updateProfile: async (data: IProfile) => {
    const response = await baseApi.put(AUTH_ROUTES.PROFILE, data);
    return response;
  },

  googleSuccess: async (token: string) => {
    const response = await baseApi.post(
      `${AUTH_ROUTES.GOOGLE_SUCCESS}/${token}`
    );
    return response;
  },

  getUserState: async () => {
    const response = await baseApi.get(AUTH_ROUTES.GET_USER_STATE);
    return response;
  },

  /**
   * Get available phone numbers with optional zipcode filtering
   * @param phoneNumberType - Type of phone number ("regular" or "toll_free")
   * @param quantity - Number of phone numbers to fetch
   * @param zipcode - Optional zipcode for location-based filtering (5 or 9 digit format: XXXXX or XXXXX-XXXX)
   * @returns Promise with available phone numbers
   * @example
   * // Without zipcode
   * await authApi.getAvailablePhoneNumbers({ phoneNumberType: "regular", quantity: 10 });
   *
   * // With 5-digit zipcode
   * await authApi.getAvailablePhoneNumbers({ phoneNumberType: "regular", quantity: 10, zipcode: "12345" });
   *
   * // With 9-digit zipcode
   * await authApi.getAvailablePhoneNumbers({ phoneNumberType: "regular", quantity: 10, zipcode: "12345-6789" });
   */
  getAvailablePhoneNumbers: async ({
    phoneNumberType,
    quantity,
    zipcode,
  }: {
    phoneNumberType: string;
    quantity: number;
    zipcode?: string;
  }) => {
    // Validate zipcode format if provided
    if (zipcode) {
      const zipcodeValidation = validateZipcode(zipcode);
      if (!zipcodeValidation.isValid) {
        throw new Error(zipcodeValidation.error);
      }
    }

    // Build query parameters
    const params = new URLSearchParams({
      phone_number_type: phoneNumberType,
      quantity: quantity.toString(),
    });

    // Add zipcode to params if provided
    if (zipcode) {
      params.append("zip_code", zipcode);
    }

    const response = await baseApi.get(
      `${AUTH_ROUTES.GET_AVAILABLE_PHONE_NUMBERS}?${params.toString()}`
    );
    console.log("response from getAvailablePhoneNumbers", response);
    return response;
  },

  getPackages: async (packageType: string) => {
    const response = await baseApi.get(
      `${AUTH_ROUTES.GET_AVAILABLE_PACKAGES}/?package_type=${packageType}`
    );
    return response;
  },

  purchasePackage: async ({
    id,
    phoneNumber,
  }: {
    id: string;
    phoneNumber: string;
  }) => {
    const url = AUTH_ROUTES.PURCHASE_PACKAGE.replace(":id", id).replace(
      ":phone_number",
      phoneNumber
    );
    console.log("url", url);
    const response = await baseApi.post(url);
    console.log("response from purchasePackage", response);
    return response;
  },

  purchaseSuccess: async ({ session_id }: { session_id: string }) => {
    const url = AUTH_ROUTES.PURCHASE_PACKAGE_SUCCESS.replace(
      ":session_id",
      session_id
    );
    console.log("url", url);
    const response = await baseApi.get(url);
    return response;
  },

  logout: async () => {
    const refresh_token = localStorage.getItem("refresh_token");
    const access_token = localStorage.getItem("access_token");
    const response = await baseApi.post(AUTH_ROUTES.LOGOUT, {
      headers: {
        Authorization: refresh_token,
      },
      access_token,
    });
    return response;
  },

  deleteAccount: async () => {
    const response = await baseApi.delete(AUTH_ROUTES.DELETE_ACCOUNT);
    return response;
  },
};

// React Query hooks

export function useLogout() {
  return useMutation({
    mutationFn: () => authApi.logout(),
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: () => authApi.deleteAccount(),
  });
}

export function useGetMe() {
  return useQuery({ queryKey: ["me"], queryFn: authApi.getMe });
}

export function useLoginByEmailPassword() {
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.loginByEmailPassword(email, password),
  });
}

export function useRegisterByEmailPassword() {
  return useMutation({
    mutationFn: ({
      email,
      password,
      confirm_password,
    }: {
      email: string;
      password: string;
      confirm_password: string;
    }) => authApi.registerByEmailPassword(email, password, confirm_password),
  });
}

export function useVerifyOTP() {
  return useMutation({
    mutationFn: ({ email, otp }: { email: string; otp: string }) =>
      authApi.verifyOTP(email, otp),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => authApi.verifyEmail(token),
  });
}

export function useCompletePersonalData() {
  return useMutation({
    mutationFn: ({
      firstName,
      lastName,
    }: {
      firstName: string;
      lastName: string;
    }) => authApi.completePersonalData(firstName, lastName),
  });
}

export function useCompleteBusinessData() {
  return useMutation({
    mutationFn: ({
      businessName,
      businessEmails,
      businessPhones,
      address,
    }: {
      businessName: string;
      businessEmails: string[];
      businessPhones: string[];
      address: {
        country?: string;
        city: string;
        state: string;
        line_1: string;
        line_2?: string | null;
        zip_code?: string;
      };
    }) =>
      authApi.completeBusinessData(
        businessName,
        businessEmails,
        businessPhones,
        address
      ),
  });
}

export function useGoogleSuccess() {
  return useMutation({
    mutationFn: (token: string) => authApi.googleSuccess(token),
  });
}

export function useUserState() {
  return useQuery({ queryKey: ["userState"], queryFn: authApi.getUserState });
}

/**
 * React Query hook for fetching available phone numbers with optional zipcode filtering
 * @param params - Object containing phoneNumberType, quantity, and optional zipcode
 * @returns React Query result with available phone numbers
 * @example
 * // Without zipcode
 * const { data, isLoading } = useAvailablePhoneNumbers({
 *   phoneNumberType: "regular",
 *   quantity: 10
 * });
 *
 * // With zipcode
 * const { data, isLoading } = useAvailablePhoneNumbers({
 *   phoneNumberType: "regular",
 *   quantity: 10,
 *   zipcode: "12345"
 * });
 */
export function useAvailablePhoneNumbers(params: {
  phoneNumberType: string;
  quantity: number;
  zipcode?: string;
}) {
  return useQuery({
    queryKey: ["availablePhoneNumbers", params],
    queryFn: () => authApi.getAvailablePhoneNumbers(params),
  });
}

export function usePackages(packageType: string) {
  return useQuery({
    queryKey: ["packages", packageType],
    queryFn: () => authApi.getPackages(packageType),
  });
}

export function usePurchasePackage() {
  return useMutation({
    mutationFn: ({ id, phoneNumber }: { id: string; phoneNumber: string }) =>
      authApi.purchasePackage({ id, phoneNumber }),
  });
}

export function usePurchaseSuccess(session_id: string) {
  return useQuery({
    queryKey: ["purchaseSuccess", session_id],
    queryFn: () => authApi.purchaseSuccess({ session_id }),
  });
}

export const authApiHooks = {
  useGetMe,
  useLoginByEmailPassword,
  useRegisterByEmailPassword,
  useVerifyOTP,
  useVerifyEmail,
  useCompletePersonalData,
  useCompleteBusinessData,
  useGoogleSuccess,
  useUserState,
  useAvailablePhoneNumbers,
  usePackages,
  usePurchasePackage,
  usePurchaseSuccess,
  useLogout,
  useDeleteAccount,
} as const;

export { authApi };
